import React from "react";
import { YAxis as ReChartsYAxis } from "recharts";
import type { NumberDomain } from "recharts/types/util/types";

import { type FormatterUnit, formatUnits } from "~/tools/formatter-unit";

import { getMinimumPrecisionForUniqueTickLabels } from "./_y-axis-helpers";

const Y_AXIS_TICK_COUNT = 4;
const MAX_AUTOMATIC_PRECISION = 100;
const BASELINE_VARIANCE_DIVISOR = 3;

export interface YAxisProps {
	unit: FormatterUnit;
	decimalPlaces: undefined | number;
	labelWidth: number;
	paddingTop: number;

	min?: number;
	max?: number;

	/**
	 * @default true
	 */
	show?: boolean;

	/**
	 * @default ""
	 */
	label?: string;
}

export const YAxis: React.FC<YAxisProps> = React.memo(
	({ unit, decimalPlaces, labelWidth, paddingTop, show = true, label = "", min, max }) => {
		const showLabel = label.length > 0;
		const precisionRef = React.useRef<undefined | number>(decimalPlaces);
		precisionRef.current = decimalPlaces;

		const domain = React.useCallback(
			([dataMin, dataMax]: NumberDomain): NumberDomain => {
				let effectiveMin = min ?? dataMin;
				let effectiveMax = max ?? dataMax;

				if (min == null && max == null && dataMin >= 0) {
					// make sure the values in the graph use at least 1/3 (BASELINE_VARIANCE_DIVISOR)
					// of the available vertical area.  If this is the case when starting
					// at 0 (zero), do so.

					const variance = dataMax - dataMin;
					if (variance > dataMax / BASELINE_VARIANCE_DIVISOR || variance === 0) {
						effectiveMin = 0;
						effectiveMax = dataMax;
					} else {
						effectiveMin = dataMin - 2 * variance;
						effectiveMax = dataMax;
					}
				}

				// If no explicit decimal places are set, calculate the minimum number of
				// precision for unique tick labels up until the maximum supported
				if (decimalPlaces == null) {
					precisionRef.current =
						getMinimumPrecisionForUniqueTickLabels({
							unit,
							min: effectiveMin,
							max: effectiveMax,
							tickCount: Y_AXIS_TICK_COUNT,
							maxPrecision: MAX_AUTOMATIC_PRECISION,
						}) ?? 0;
				}

				return [effectiveMin, effectiveMax];
			},
			[decimalPlaces, min, max, unit],
		);

		const formatValue = React.useCallback(
			(value: number) => formatUnits(unit, value, { precision: precisionRef.current }),
			[unit],
		);

		const hasSameMinAndMax = min != null && max != null && min === max;

		return (
			<ReChartsYAxis
				allowDataOverflow={min != null || max != null}
				axisLine={false}
				domain={domain}
				fontSize={10}
				hide={!show}
				label={
					showLabel
						? // https://github.com/recharts/recharts/issues/466#issuecomment-323747984
							{
								value: label,
								angle: -90,
								position: "insideLeft",
								textAnchor: "middle",
							}
						: undefined
				}
				mirror
				padding={{ top: paddingTop }}
				tick={(props) => <CustomTick {...props} transform="translate(-5, -5)" />}
				tickCount={hasSameMinAndMax ? 1 : Y_AXIS_TICK_COUNT}
				tickFormatter={formatValue}
				// that adjusts alignment of the ticks
				tickLine={false}
				// that prevents the top tick cutting off of the chart area
				tickMargin={showLabel ? labelWidth - 1 : -1}
				ticks={hasSameMinAndMax ? [min] : undefined}
				type="number"
				width={120}
			/>
		);
	},
);

if (import.meta.env.DEV) {
	YAxis.displayName = "TimeSeriesChart.YAxis";
}

const CustomTick: React.FC<{
	x: number;
	y: number;
	width: number;
	height: number;
	payload: { value: number };
	tickFormatter?: (value: number) => string;
	fontSize: number;
	fill: string;
}> = ({ x, y, width, height, payload, tickFormatter, fill, fontSize, ...props }) => {
	const [textRef, setTextRef] = React.useState<SVGTextElement>();
	const textClientRect = textRef?.getBoundingClientRect();
	const textHeight = textClientRect?.height ?? 0;
	const textWidth = textClientRect?.width ?? 0;
	const backgroundPadding = { x: 4, y: 2 };

	return (
		<g {...props}>
			<rect
				className="fill-white"
				dominantBaseline=""
				fillOpacity={0.8}
				height={textHeight + backgroundPadding.y * 2}
				rx={5}
				width={textWidth + backgroundPadding.x * 2}
				x={x - backgroundPadding.x}
				y={y - textHeight}
			/>

			<text
				className="fill-gray-500"
				dominantBaseline="auto"
				fontSize={fontSize}
				ref={(el) => {
					if (el != null) {
						setTextRef(el);
					}
				}}
				textAnchor="start"
				x={x}
				y={y - 1}
			>
				{tickFormatter ? tickFormatter(payload.value) : payload.value}
			</text>
		</g>
	);
};
